import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { useChatHistory } from '@/hooks/useChatHistory';
import { deleteMessage as apiDeleteMessage } from '@/api/fleet';
import { useToast } from '@/hooks/useToast';
import ChatMessage from './ChatMessage';
import LoadingMessage from './LoadingMessage';
import WelcomeMessage from './WelcomeMessage';
import Spinner from '@/components/ui/Spinner';
import ContextMenu from './ContextMenu';

export default function ChatMessages() {
  const messages = useChatStore((s) => s.messages);
  const removeMessage = useChatStore((s) => s.removeMessage);
  const getSessionId = useAuthStore((s) => s.getSessionId);
  const { loadingHistory } = useChatHistory();
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Only the LAST AI message with fleet records should show the fleet button
  const lastFleetMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender !== 'user' && (m.records?.length ?? 0) > 0) {
        return m.id;
      }
    }
    return null;
  }, [messages]);

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    messageId: string;
  } | null>(null);

  // Auto-scroll to bottom or message start
  useEffect(() => {
    if (!scrollRef.current) return;

    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender !== 'user') {
        const el = document.getElementById(`message-${lastMessage.id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    }

    // Fallback: scroll to bottom
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, messageId: string) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, messageId });
    },
    []
  );

  const handleCopy = useCallback(
    (messageId: string) => {
      const msg = messages.find((m) => m.id === messageId);
      if (!msg) return;

      const text = msg.content;

      // Primary: clipboard API (requires secure context)
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(
          () => toast('Copiado al portapapeles', 'success'),
          () => {
            // Fallback for non-secure context or permission denied
            fallbackCopy(text);
          }
        );
      } else {
        fallbackCopy(text);
      }

      function fallbackCopy(value: string) {
        try {
          const ta = document.createElement('textarea');
          ta.value = value;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
          toast('Copiado al portapapeles', 'success');
        } catch {
          toast('Error al copiar', 'error');
        }
      }
    },
    [messages, toast]
  );

  const handleDelete = useCallback(
    async (messageId: string) => {
      try {
        const sessionId = getSessionId();
        await apiDeleteMessage(sessionId, messageId);
        removeMessage(messageId);
        toast('Mensaje eliminado', 'success');
      } catch {
        toast('Error al eliminar mensaje', 'error');
      }
    },
    [getSessionId, removeMessage, toast]
  );

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-4">
      {loadingHistory && messages.length === 0 && (
        <div className="flex items-center justify-center py-8 gap-2">
          <Spinner size="sm" />
          <span className="text-xs text-text-muted">Cargando historial...</span>
        </div>
      )}

      {!loadingHistory && messages.length === 0 && <WelcomeMessage />}

      {messages.map((msg, index) => (
        <ChatMessage
          key={msg.id}
          message={msg}
          previousMessage={index > 0 ? messages[index - 1] : undefined}
          onContextMenu={handleContextMenu}
          isLastFleetMessage={msg.id === lastFleetMessageId}
          isLatestMessage={index === messages.length - 1}
        />
      ))}

      <LoadingMessage />

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          messageId={contextMenu.messageId}
          onClose={() => setContextMenu(null)}
          onCopy={handleCopy}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
